import { api } from '../../services/apiService';
import { expandFeatures } from '@platform/shared-types';

/**
 * WHICH POS BUTTONS A TENANT IS ENTITLED TO.
 *
 * Lifted out of `PosButtonLayoutTab` unchanged so the order screen's setup
 * runner enables exactly the set that tab would show. Two copies of this would
 * drift the first time a package gained a button, and the symptom — a till set
 * up automatically carrying a different set from one set up by hand — is not
 * one anybody would think to look here for.
 *
 * `ALL_BUTTON_IDS` is passed in rather than re-declared, because the registry
 * itself (id, featureGate, label, colour, icon) belongs to the tab that draws
 * it and mirrors POS_BUTTON_REGISTRY in shared-types.
 */

/** Hardcoded package→button mapping (mirrors FEATURE_PACKAGE_MAP in shared-types).
 *  Used as a fallback when DB packages don't include pos.button.* features yet. */
const PACKAGE_BUTTON_MAP: Record<string, string[]> = {
  pkg_pos_terminal: ['void', 'refund', 'exchange', 'discount', 'hold', 'open-order', 'search', 'end-shift', 'open-drawer', 'safe-drop', 'payout', 'new-product-request', 'calculator', 'clock', 'shift-info', 'weighted-lookup', 'non-scannable-lookup'],
  pkg_pos_customers: ['customer', 'rewards', 'cash-tip'],
  pkg_pos_marketing: ['deals'],
  pkg_pos_stock: ['damage', 'pick-list', 'transfer'],
  pkg_pos_reports: ['reports', 'transactions'],
  pkg_mn7wqu8z_u8gnw6: ['store-supply'],
  pkg_pos_lottery: ['lottery'],
  pkg_services: ['services'],
};

/** Buttons whose featureGate isn't in the DB yet — included when the tenant
 *  has another button from the same package group. */
const IMPLICIT_BUTTONS: Record<string, string> = {
  'weighted-lookup': 'pos.button.search',
  'non-scannable-lookup': 'pos.button.search',
  payout: 'pos.button.safe-drop',
  'new-product-request': 'pos.button.safe-drop',
  'pick-list': 'pos.button.damage',
  transfer: 'pos.button.damage',
};

export interface EntitlementButton { id: string; featureGate: string }

export async function resolveAllowedButtonIds(
  tenantId: string,
  allButtons: readonly EntitlementButton[],
): Promise<string[]> {
  const everything = allButtons.map((b) => b.id);
  try {
    const pkgData: any = await api.superAdmin.getTenantPackages(tenantId);

    // Every feature the tenant's packages carry, read from all three shapes
    // the endpoint has returned over time.
    const allFeatures = new Set<string>();
    for (const ap of pkgData.assignedPackages || []) {
      for (const f of ap.packageDetails?.features || []) allFeatures.add(f);
    }
    for (const pkg of pkgData.packageDetails || []) {
      for (const f of pkg.features || []) allFeatures.add(f);
    }
    const enabledSet = new Set(pkgData.tenant?.enabledPackages || []);
    for (const pkg of pkgData.allPackages || []) {
      if (enabledSet.has(pkg.id)) for (const f of pkg.features || []) allFeatures.add(f);
    }

    const expandedFeatures = new Set(expandFeatures([...allFeatures]));
    const fromFeatures = allButtons
      .filter((btn) => expandedFeatures.has(btn.featureGate))
      .map((btn) => btn.id);

    const enabledPkgs: string[] = pkgData.tenant?.enabledPackages || [];
    const fromPkgs = new Set<string>();
    for (const pkgId of enabledPkgs) {
      const buttons = PACKAGE_BUTTON_MAP[pkgId];
      if (buttons) buttons.forEach((b) => fromPkgs.add(b));
    }
    for (const [btnId, siblingGate] of Object.entries(IMPLICIT_BUTTONS)) {
      if (allFeatures.has(siblingGate)) fromPkgs.add(btnId);
    }

    const merged = new Set([...fromFeatures, ...fromPkgs]);
    if (merged.size > 0) return [...merged];

    // Any POS package at all → show everything (graceful fallback).
    if (enabledPkgs.some((p) => p.startsWith('pkg_pos_'))) return everything;

    // Nothing matched — this tenant has no POS buttons.
    return [];
  } catch {
    // A failed package read must not present an empty till as the answer.
    return everything;
  }
}
